import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaBolt, FaClock, FaLaptop, FaLock, FaCheckCircle } from 'react-icons/fa';

const Security = () => {
  const [activeFeature, setActiveFeature] = useState(0);

  // Memoize static data
  const features = useMemo(() => [
    {
      icon: <FaShieldAlt className="text-4xl" />,
      title: 'Lead-Safe Protection',
      desc: 'Secure sign-ins, spam blockers, clean data checks, and fake lead spotters keep your customer info safe from prying eyes.',
      color: 'from-purple-500 to-pink-500',
      bgColor: 'from-purple-50 to-pink-50',
      accentColor: 'purple',
      stats: ['Strong Data Locks', 'Safe Sign-Ins', 'Spam & Fake Blockers'],
    },
    {
      icon: <FaBolt className="text-4xl" />,
      title: 'Fast Lead Handling',
      desc: 'Process hundreds of leads every hour without slowdowns—grows easily as your business picks up speed.',
      color: 'from-yellow-500 to-orange-500',
      bgColor: 'from-yellow-50 to-orange-50',
      accentColor: 'orange',
      stats: ['Hundreds of Leads/Hour', 'Quick Responses', 'Grows with You'],
    },
    {
      icon: <FaClock className="text-4xl" />,
      title: 'Always Ready Access',
      desc: 'Up and running 99% of the time on phones, tablets, or computers, with simple backups and easy checks.',
      color: 'from-green-500 to-emerald-500',
      bgColor: 'from-green-50 to-emerald-50',
      accentColor: 'green',
      stats: ['99% Always Working', 'Easy Backups', 'Non-Stop Checks'],
    },
    {
      icon: <FaLaptop className="text-4xl" />,
      title: 'Works Anywhere',
      desc: 'Runs smoothly on Windows, Mac, or Linux computers, plus offline mode for spotty internet days.',
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'from-blue-50 to-cyan-50',
      accentColor: 'blue',
      stats: ['Any Device', 'Offline Option', 'Easy Sync'],
    },
  ], []);

  // Respect reduced motion
  const prefersReducedMotion = useMemo(() => 
    window.matchMedia('(prefers-reduced-motion: reduce)').matches, []
  );

  const containerVariants = {
    hidden: prefersReducedMotion ? { opacity: 1 } : { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: prefersReducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 py-32">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Rotating shield patterns - CSS */}
        <div 
          className="absolute top-1/4 left-1/4 w-96 h-96 [animation:css-shield-rotate 30s linear infinite]"
          style={{ transform: 'translateZ(0)' }}
        >
          <div className="absolute inset-0 border-2 border-purple-500/10 rounded-full" />
          <div className="absolute inset-8 border-2 border-pink-500/10 rounded-full" />
          <div className="absolute inset-16 border-2 border-cyan-500/10 rounded-full" />
        </div>

        <div 
          className="absolute bottom-1/4 right-1/4 w-96 h-96 [animation:css-shield-rotate-reverse 40s linear infinite]"
          style={{ transform: 'translateZ(0)' }}
        >
          <div className="absolute inset-0 border-2 border-orange-500/10 rounded-full" />
          <div className="absolute inset-8 border-2 border-green-500/10 rounded-full" />
          <div className="absolute inset-16 border-2 border-blue-500/10 rounded-full" />
        </div>

        {/* Fewer floating particles - CSS */}
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white/20 rounded-full [animation:css-particle-float 3s ease-in-out infinite]"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${3 + Math.random() * 2}s`,
              transform: 'translateZ(0)',
            }}
          />
        ))}

        {/* Mesh gradient blobs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl [animation:css-pulse 4s ease-in-out infinite]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl [animation:css-pulse 4s ease-in-out infinite] [animation-delay:1s]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl [animation:css-pulse 4s ease-in-out infinite] [animation-delay:2s]" />
      </div>

      {/* Static grid overlay */}
      <div className="absolute inset-0 opacity-10">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '50px 50px',
            transform: 'translateZ(0)',
          }}
        />
      </div>

      <div className="relative container mx-auto px-8 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
          animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.9 }}
            animate={prefersReducedMotion ? false : { opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6"
          >
            <motion.div
              className="will-change-transform"
              style={{ transform: 'translateZ(0)' }}
              animate={prefersReducedMotion ? false : { rotate: [0, 360] }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            >
              <FaShieldAlt className="text-green-400" />
            </motion.div>
            <span className="text-sm font-bold text-white/90">Lead-Safe & Worry-Free</span>
          </motion.div>

          <h2 className="text-5xl md:text-7xl font-black mb-6 leading-tight text-white">
            <span className="block">Rock-Solid &</span>
            <span className="block bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent animate-gradient">
              Reliable
            </span>
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Designed to keep your leads secure and your business running smoothly—no tech headaches, just peace of mind.
          </p>
        </motion.div>

        {/* Main Content - Split Layout */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left: Feature Cards */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-4"
          >
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                onClick={() => setActiveFeature(idx)}
                className={`relative cursor-pointer group transition-all duration-500 will-change-transform ${
                  activeFeature === idx ? 'scale-105' : 'scale-100'
                }`}
                style={{ transform: 'translateZ(0)' }}
              >
                <div className={`relative bg-white/5 backdrop-blur-md border-2 rounded-2xl p-6 overflow-hidden transition-all ${
                  activeFeature === idx 
                    ? 'border-white/30 shadow-2xl' 
                    : 'border-white/10 hover:border-white/20'
                }`}>
                  {/* Gradient overlay on active */}
                  {activeFeature === idx && (
                    <motion.div
                      className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-10 will-change-transform`}
                      style={{ transform: 'translateZ(0)' }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 0.1 }}
                      transition={{ duration: 0.5 }}
                    />
                  )}

                  <div className="relative flex items-start gap-5">
                    {/* Icon */}
                    <motion.div
                      className={`p-4 bg-gradient-to-br ${feature.color} rounded-2xl shadow-2xl flex-shrink-0 text-white will-change-transform`}
                      style={{ transform: 'translateZ(0)' }}
                      whileHover={prefersReducedMotion ? false : { scale: 1.1, rotate: 5 }}
                      animate={activeFeature === idx ? { scale: [1, 1.05, 1] } : {}}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      {feature.icon}
                    </motion.div>

                    {/* Content */}
                    <div className="flex-1 pt-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-white">{feature.title}</h3>
                        {activeFeature === idx && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 500 }}
                          >
                            <FaCheckCircle className="text-green-400" />
                          </motion.div>
                        )}
                      </div>
                      <p className="text-slate-300 text-sm leading-relaxed mb-3">
                        {feature.desc}
                      </p>

                      {/* Mini stats badges */}
                      <div className="flex flex-wrap gap-2">
                        {feature.stats.map((stat, statIdx) => (
                          <motion.div
                            key={statIdx}
                            initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.8 }}
                            animate={activeFeature === idx ? { opacity: 1, scale: 1 } : { opacity: 0.5, scale: 0.9 }}
                            transition={{ delay: statIdx * 0.1 }}
                            className={`px-3 py-1 rounded-full text-xs font-semibold will-change-transform ${
                              activeFeature === idx
                                ? `bg-gradient-to-r ${feature.color} text-white`
                                : 'bg-white/5 text-slate-400'
                            }`}
                            style={{ transform: 'translateZ(0)' }}
                          >
                            {stat}
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Active indicator */}
                  {activeFeature === idx && (
                    <motion.div
                      className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${feature.color} will-change-transform`}
                      style={{ transform: 'translateZ(0)' }}
                      initial={{ height: 0 }}
                      animate={{ height: '100%' }}
                      transition={{ duration: 0.5 }}
                    />
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Right: Interactive Security Shield */}
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.9 }}
            animate={prefersReducedMotion ? false : { opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative sticky top-8"
          >
            {/* Central Shield Visual */}
            <div className="relative aspect-square max-w-md mx-auto">
              {/* Rotating rings - CSS */}
              <div 
                className="absolute inset-0 [animation:css-ring-rotate 20s linear infinite]"
                style={{ transform: 'translateZ(0)' }}
              >
                <div className={`absolute inset-0 rounded-full border-4 border-dashed opacity-20 bg-gradient-to-r ${features[activeFeature].color}`} />
              </div>

              <div 
                className="absolute inset-8 [animation:css-ring-rotate-reverse 30s linear infinite]"
                style={{ transform: 'translateZ(0)' }}
              >
                <div className={`absolute inset-0 rounded-full border-2 opacity-30 bg-gradient-to-r ${features[activeFeature].color}`} />
              </div>

              {/* Central shield */}
              <motion.div
                className="absolute inset-0 flex items-center justify-center will-change-transform"
                key={activeFeature}
                style={{ transform: 'translateZ(0)' }}
                initial={prefersReducedMotion ? false : { scale: 0, rotate: -180 }}
                animate={prefersReducedMotion ? false : { scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <div className={`relative w-64 h-64 bg-gradient-to-br ${features[activeFeature].color} rounded-3xl shadow-2xl flex items-center justify-center transform rotate-45`}>
                  <motion.div
                    className="transform -rotate-45 will-change-transform"
                    style={{ transform: 'translateZ(0)' }}
                    animate={prefersReducedMotion ? false : { scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <div className="text-white text-8xl">
                      {features[activeFeature].icon}
                    </div>
                  </motion.div>

                  {/* Glowing corners - Reduced to 4, CSS pulse */}
                  {[0, 1, 2, 3].map((corner) => (
                    <div
                      key={corner}
                      className="absolute w-4 h-4 bg-white rounded-full [animation:css-pulse 2s ease-in-out infinite]"
                      style={{
                        top: corner < 2 ? '-8px' : 'auto',
                        bottom: corner >= 2 ? '-8px' : 'auto',
                        left: corner % 2 === 0 ? '-8px' : 'auto',
                        right: corner % 2 === 1 ? '-8px' : 'auto',
                        animationDelay: `${corner * 0.5}s`,
                        transform: 'translateZ(0)',
                      }}
                    />
                  ))}
                </div>
              </motion.div>

              {/* Orbiting feature badges */}
              {features.map((feature, idx) => {
                const angle = (idx * 360) / features.length;
                const radius = 180;
                const x = Math.cos((angle * Math.PI) / 180) * radius;
                const y = Math.sin((angle * Math.PI) / 180) * radius;

                return (
                  <motion.div
                    key={idx}
                    className="absolute top-1/2 left-1/2 cursor-pointer will-change-transform"
                    style={{ 
                      left: `calc(50% + ${x}px)`,
                      top: `calc(50% + ${y}px)`,
                      transform: 'translateZ(0)',
                    }}
                    animate={{
                      scale: activeFeature === idx ? 1.2 : 1,
                      opacity: activeFeature === idx ? 1 : 0.5,
                    }}
                    transition={{ duration: 0.5 }}
                  >
                    <motion.div
                      className={`w-12 h-12 rounded-full bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-2xl border-4 border-slate-900 will-change-transform`}
                      style={{ transform: 'translateZ(0)' }}
                      whileHover={prefersReducedMotion ? false : { scale: 1.3 }}
                      onClick={() => setActiveFeature(idx)}
                      animate={prefersReducedMotion ? false : {
                        rotate: activeFeature === idx ? [0, 360] : 0,
                      }}
                      transition={{
                        rotate: { duration: 1, repeat: activeFeature === idx ? Infinity : 0 },
                      }}
                    >
                      <div className="text-white text-lg">
                        {React.cloneElement(feature.icon, { className: 'text-lg' })}
                      </div>
                    </motion.div>
                  </motion.div>
                );
              })}

              {/* Pulse effect from center */}
              <motion.div
                className={`absolute inset-0 rounded-full bg-gradient-to-r ${features[activeFeature].color} opacity-20 will-change-transform`}
                style={{ transform: 'translateZ(0)' }}
                animate={prefersReducedMotion ? false : {
                  scale: [1, 1.5, 1],
                  opacity: [0.2, 0, 0.2],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>

            {/* Stats display below shield */}
            <motion.div
              key={activeFeature}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
              animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
              className="mt-12 space-y-3"
            >
              {features[activeFeature].stats.map((stat, idx) => (
                <motion.div
                  key={idx}
                  initial={prefersReducedMotion ? false : { opacity: 0, x: 20 }}
                  animate={prefersReducedMotion ? false : { opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 will-change-transform"
                  style={{ transform: 'translateZ(0)' }}
                >
                  <motion.div
                    className={`w-10 h-10 rounded-lg bg-gradient-to-br ${features[activeFeature].color} flex items-center justify-center will-change-transform`}
                    style={{ transform: 'translateZ(0)' }}
                    animate={prefersReducedMotion ? false : { rotate: [0, 360] }}
                    transition={{ duration: 3, repeat: Infinity, delay: idx * 0.5 }}
                  >
                    <FaLock className="text-white" />
                  </motion.div>
                  <span className="text-white font-semibold">{stat}</span>
                  <motion.div
                    className="ml-auto will-change-transform"
                    style={{ transform: 'translateZ(0)' }}
                    animate={prefersReducedMotion ? false : { scale: [1, 1.2, 1] }}
                    transition={{ duration: 1, repeat: Infinity, delay: idx * 0.3 }}
                  >
                    <FaCheckCircle className="text-green-400" />
                  </motion.div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>

      </div>

      <style>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s linear infinite;
        }
        @keyframes css-pulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        @keyframes css-shield-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes css-shield-rotate-reverse {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }
        @keyframes css-particle-float {
          0%, 100% { 
            transform: translateY(0) opacity: 0.2; 
          }
          50% { 
            transform: translateY(-30px) opacity: 0.5; 
          }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>
    </section>
  );
};

export default Security;